// On-boot re-registration of every persisted sample.
//
// Runs once per page load after the engine is ready and Dirt + soundfonts
// are wired. For each Sample in IDB: pull its blob, decode it, register a
// playback handler. Failures are tallied but never rejected — a missing
// blob or a decode error degrades gracefully (the sample won't play but
// the rest of the library still boots).
//
// Rehydrating N samples costs N decodes but they run in parallel —
// AudioContext's decoder is off the main thread.

import { registerSampleBlob } from './register';
import type { SampleStorage } from './storage/idb';
import type { Sample } from './types';

export type RehydrateResult = {
  registered: number;
  failed: number;
  failures: Array<{ id: string; name: string; reason: string }>;
};

export async function rehydrateLocalSamples(
  ctx: AudioContext,
  storage: SampleStorage,
): Promise<RehydrateResult> {
  const samples = await storage.listSamples();
  const outcomes = await Promise.all(
    samples.map((s) => rehydrateOne(s, ctx, storage)),
  );
  const failures = outcomes.flatMap((o) =>
    o.ok ? [] : [{ id: o.sample.id, name: o.sample.name, reason: o.reason }],
  );
  return {
    registered: outcomes.filter((o) => o.ok).length,
    failed: failures.length,
    failures,
  };
}

type Outcome =
  | { ok: true; sample: Sample }
  | { ok: false; sample: Sample; reason: string };

async function rehydrateOne(
  sample: Sample,
  ctx: AudioContext,
  storage: SampleStorage,
): Promise<Outcome> {
  try {
    const blob = await storage.getBlob(sample.storageKey);
    if (!blob) {
      return { ok: false, sample, reason: 'blob missing' };
    }
    await registerSampleBlob(ctx, blob, {
      name: sample.name,
      pitchedRootMidi: sample.pitchedRootMidi,
    });
    return { ok: true, sample };
  } catch (e) {
    return {
      ok: false,
      sample,
      reason: e instanceof Error ? e.message : String(e),
    };
  }
}
